import { useContext, useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import Toolbar from './toolbar';
import DynamicIcon from './dynamic-icon';
import DateRangeFilter from './date-range-filter';
import TableFilterContext from '../../contexts/table-filter-context';
import { serializeRange, parseRange } from '../../lib/date-buckets';

export const CREATED_AT_FILTER_ID = 'created_at';

// The pseudo-column the filter system treats like any other filterable field.
// `created_at` lives on the row itself rather than in `data`, so it has no real
// column to hang off.
export const CREATED_AT_COLUMN = {
    id: CREATED_AT_FILTER_ID,
    name: 'Date created',
    type: CREATED_AT_FILTER_ID,
};

const describeRange = (range) => {
    if (!range?.from) return null;
    const from = format(range.from, 'd MMM');
    if (!range.to || format(range.from, 'yyyy-MM-dd') === format(range.to, 'yyyy-MM-dd')) return from;
    return `${from} – ${format(range.to, 'd MMM')}`;
};

const CreatedAtFilterType = ({ column, handleFilterChange, handleRemoveFilter }) => {
    const { newFilterId, filterValues, setFilterValues } = useContext(TableFilterContext);
    const rawValue = filterValues ? filterValues[column.id] : '';
    const range = parseRange(rawValue);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        if (newFilterId === column.id) setIsOpen(true);
    }, [newFilterId, column.id]);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const applyRange = (next) => {
        const serialized = serializeRange(next);
        setFilterValues && setFilterValues({ ...filterValues, [column.id]: serialized });
        handleFilterChange && handleFilterChange(column.id, serialized);
    };

    const clearRange = () => {
        setFilterValues && setFilterValues({ ...filterValues, [column.id]: '' });
        handleFilterChange && handleFilterChange(column.id, '');
        setIsOpen(false);
    };

    const onRemoveFilter = (e) => {
        e.stopPropagation();
        handleRemoveFilter(column.id);
        setIsOpen(false);
    };

    const label = describeRange(range) || column.name;

    return (
        <div className="relative" ref={containerRef}>
            <Toolbar onClick={() => setIsOpen(!isOpen)} additionalclasses="!px-[12px]">
                <div className="flex gap-[8px] items-center">
                    <div className="flex gap-[4px] items-center whitespace-nowrap">
                        <DynamicIcon name="chevron-down" size={16} />
                        {label}
                    </div>
                    <DynamicIcon onClick={onRemoveFilter} name="x" size={14} />
                </div>
            </Toolbar>
            {isOpen && (
                <DateRangeFilter
                    value={range}
                    onApply={applyRange}
                    onClear={clearRange}
                    onClose={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

export default CreatedAtFilterType;
